/**
 * Utility functions for managing cursor position in contentEditable elements
 */

/**
 * Get the current cursor position as an offset from the start of the text content
 */
export const getCursorPosition = (element: HTMLDivElement): number => {
  const selection = window.getSelection();
  let cursorPosition = 0;
  
  if (selection && selection.rangeCount > 0) {
    const range = selection.getRangeAt(0);
    
    // Calculate cursor position relative to the text content
    const walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_TEXT,
      null
    );
    
    let currentOffset = 0;
    let node;
    while ((node = walker.nextNode()) !== null) {
      if (node === range.startContainer) {
        cursorPosition = currentOffset + range.startOffset;
        break;
      }
      currentOffset += node.textContent?.length || 0;
    }
  }
  
  return cursorPosition;
};

/**
 * Restore cursor position to a specific offset in the text content
 */
export const restoreCursorPosition = (
  element: HTMLDivElement, 
  targetPosition: number
): boolean => {
  try {
    const selection = window.getSelection();
    if (!selection) return false;
    
    const range = document.createRange();
    const walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_TEXT,
      null
    );
    
    let currentOffset = 0;
    let targetNode = null;
    let targetOffset = 0;
    
    let node;
    while ((node = walker.nextNode()) !== null) {
      const nodeLength = node.textContent?.length || 0;
      if (currentOffset + nodeLength >= targetPosition) {
        targetNode = node;
        targetOffset = targetPosition - currentOffset;
        break;
      }
      currentOffset += nodeLength;
    }
    
    if (targetNode) {
      range.setStart(targetNode, targetOffset);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      return true;
    }
  } catch (error) {
    console.log('Could not restore cursor position:', error);
  }
  
  return false;
};

/**
 * Check if there is a text selection (not just a cursor position)
 */
export const hasTextSelection = (): boolean => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return false;
  
  // Check if the selection is collapsed (just a cursor) or has actual selected text
  return !selection.isCollapsed && selection.toString().length > 0;
};

/**
 * Check if cursor is at the beginning of the content
 * Returns false if there's a text selection (we want to allow deletion of selections)
 */
export const isCursorAtStart = (element: HTMLDivElement): boolean => {
  const selection = window.getSelection();
  if (!selection) return false;
  
  // If there's a text selection, don't consider it "at start" - allow normal deletion
  if (hasTextSelection()) return false;
  
  return selection.anchorOffset === 0;
};

/**
 * Check if cursor is at the end of the content
 * Returns false if there's a text selection (we want to allow deletion of selections)
 */
export const isCursorAtEnd = (element: HTMLDivElement): boolean => {
  const selection = window.getSelection();
  if (!selection) return false;
  
  // If there's a text selection, don't consider it "at end" - allow normal deletion
  if (hasTextSelection()) return false;
  
  return selection.anchorOffset === element.innerText.length;
};

/**
 * Save and restore cursor position around a function that modifies content
 */
export const withCursorPosition = <T>(
  element: HTMLDivElement,
  fn: () => T
): T => {
  const cursorPosition = getCursorPosition(element);
  const result = fn();
  
  // Restore cursor position after a short delay to ensure DOM updates are complete
  setTimeout(() => {
    restoreCursorPosition(element, cursorPosition);
  }, 0);
  
  return result;
};

/**
 * Set cursor to the end of the content in a contentEditable element
 */
export const setCursorToEnd = (element: HTMLDivElement): boolean => {
  try {
    const selection = window.getSelection();
    if (!selection) return false;
    
    const range = document.createRange();
    
    // Find the last text node or position at the end of the element
    if (element.childNodes.length === 0) {
      // Empty element - position at start (which is also end)
      range.setStart(element, 0);
      range.collapse(true);
    } else {
      // Find the last text node to position cursor at the end
      const walker = document.createTreeWalker(
        element,
        NodeFilter.SHOW_TEXT,
        null
      );
      
      let lastTextNode: Node | null = null;
      let node;
      while ((node = walker.nextNode()) !== null) {
        lastTextNode = node;
      }
      
      if (lastTextNode) {
        // Position cursor at the end of the last text node
        range.setStart(lastTextNode, lastTextNode.textContent?.length || 0);
        range.collapse(true);
      } else {
        // No text nodes - position at end of element
        range.selectNodeContents(element);
        range.collapse(false);
      }
    }
    
    selection.removeAllRanges();
    selection.addRange(range);
    return true;
  } catch (error) {
    console.log('Could not set cursor to end:', error);
    return false;
  }
};

/**
 * Cursor state for saving and restoring focus and cursor position
 */
export interface CursorState {
  blockId: string;
  cursorPosition: number;
}

/**
 * Save the current cursor state (which block has focus and cursor position within it)
 * @param activeBlockId - ID of the currently active/focused block
 * @param element - The contentEditable element of the active block
 * @returns CursorState object with block ID and cursor position
 */
export const saveCursorState = (
  activeBlockId: string,
  element: HTMLDivElement | null
): CursorState => {
  const cursorPosition = element ? getCursorPosition(element) : 0;
  
  return {
    blockId: activeBlockId,
    cursorPosition,
  };
};

/**
 * Restore cursor state by focusing a block and positioning the cursor
 * @param cursorState - The saved cursor state to restore
 * @param findBlockElement - Function to find the DOM element for a given block ID
 * @param setActiveBlockId - Function to set the active block ID in the editor
 */
export const restoreCursorState = (
  cursorState: CursorState,
  findBlockElement: (blockId: string) => HTMLDivElement | null,
  setActiveBlockId: (blockId: string) => void
): void => {
  const { blockId, cursorPosition } = cursorState;
  
  // Set the active block ID (this will trigger focus in the editor)
  setActiveBlockId(blockId);
  
  // Wait for the DOM to update before positioning cursor
  setTimeout(() => {
    const blockElement = findBlockElement(blockId);
    if (blockElement) {
      blockElement.focus();
      restoreCursorPosition(blockElement, cursorPosition);
    }
  }, 0);
};
